/**
 * i18n Configuration
 * 
 * Setup i18next with German and English translations.
 * Persists language preference in localStorage.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // Common
      common: {
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        cancel: 'Cancel',
        save: 'Save',
        create: 'Create',
        update: 'Update',
        delete: 'Delete',
        edit: 'Edit',
        close: 'Close',
        search: 'Search projects...',
        filter: 'Filter',
        refresh: 'Refresh',
        submit: 'Submit',
        back: 'Back',
        next: 'Next',
        yes: 'Yes',
        no: 'No',
        view: 'View',
        download: 'Download',
        upload: 'Upload',
        add: 'Add',
        remove: 'Remove',
        clear: 'Clear',
        apply: 'Apply',
        reset: 'Reset',
        actions: 'Actions',
        status: 'Status',
        type: 'Type',
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        date: 'Date',
        time: 'Time',
        description: 'Description',
        notes: 'Notes',
        priority: 'Priority',
        assignedTo: 'Assigned To',
        createdAt: 'Created At',
        updatedAt: 'Updated At',
        total: 'total',
        all: 'All',
        none: 'None',
        notifications: 'Notifications',
        noNotifications: 'No new notifications',
        viewAll: 'View All',
        noData: 'No data',
        confirmDelete: 'Are you sure you want to delete this?',
        deleteSuccess: 'Deleted successfully',
        deleteError: 'Failed to delete',
        saveSuccess: 'Saved successfully',
        saveError: 'Failed to save',
        required: 'Required',
        unknownProject: 'Unknown Project',
        optional: 'Optional',
      },
      
      // Navigation
      nav: {
        dashboard: 'Dashboard',
        projects: 'Projects',
        vault: 'Vault',
        myTime: 'My Time',
        team: 'Team',
        tags: 'Tags',
        activityLog: 'Activity Log',
        approvals: 'Approvals',
        reports: 'Reports',
        analytics: 'Analytics',
        invoices: 'Invoices',
        profile: 'Profile',
        logout: 'Logout',
        main: 'Main',
        management: 'Management',
        timeTracking: 'Time Tracking',
        support: 'Support',
        library: 'Library',
        settings: 'Settings',
      },
      
      // Login Page
      login: {
        title: 'Welcome back',
        subtitle: 'Sign in to your account to continue',
        email: 'Email',
        emailPlaceholder: 'you@company.com',
        password: 'Password',
        passwordPlaceholder: 'Enter your password',
        signIn: 'Sign In',
        signingIn: 'Signing in...',
        forgotPassword: 'Forgot password?',
        noAccount: 'Don\'t have an account?',
        contactAdmin: 'Contact your administrator if you need access',
        welcome: 'Welcome back',
        features: {
          credentials: 'Secure Credentials',
          credentialsDesc: 'Encrypted password vault',
          monitoring: 'Health Monitoring',
          monitoringDesc: 'Real-time website status',
          team: 'Team Management',
          teamDesc: 'Role-based access control',
        },
        errors: {
          emailRequired: 'Please enter your email',
          emailInvalid: 'Please enter a valid email',
          passwordRequired: 'Please enter your password',
          invalidCredentials: 'Invalid credentials',
          loginFailed: 'Login failed',
        },
        branding: {
          tagline: 'Landeseiten Maintenance Management',
        },
      },

      // Dashboard
      dashboard: {
        greeting: {
          morning: 'Good morning',
          afternoon: 'Good afternoon',
          evening: 'Good evening',
        },
        subtitle: 'System health & team availability overview.',
        error: 'Failed to load dashboard data',
        noTimerRunning: 'No timer running',
        startTracking: 'Start tracking your time',
        startTimer: 'Start Timer',
        timerRunning: 'Timer running',
        stopTimer: 'Stop',
        overview: 'Overview',
        stats: {
          totalProjects: 'Total Projects',
          online: 'Online',
          openTodos: 'Open Todos',
          supportTickets: 'Support Tickets',
          atRisk: 'At Risk',
          hackedDown: 'Hacked / Down',
          pendingApprovals: 'Pending Approvals',
          hacked: 'Compromised',
        },
        suffixes: {
          tasks: 'Tasks',
          open: 'Open',
          warnings: 'Warnings',
          criticalAlert: 'Critical Alert',
          allClear: 'All Clear',
        },
        team: {
          availability: 'Team Availability & Impact',
          overview: 'Team Overview',
          teamMember: 'Team Member',
          name: 'Name',
          role: 'Role',
          status: 'Status',
          returnDate: 'Return Date',
          projects: 'Projects',
          project: 'Project',
          noMembers: 'No team members with this role.',
          na: 'N/A',
        },
        statuses: {
          sick: 'Sick Leave',
          vacation: 'Vacation',
          parental: 'Parental Leave',
        },
        roles: {
          admin: 'Admin',
          pm: 'PM',
          dev: 'Dev',
          viewer: 'Viewer',
        },
        filters: {
          all: 'All',
          developers: 'Developers',
          managers: 'Managers',
          admins: 'Admins',
        },
        quickActions: {
          title: 'Quick Actions',
          startTimer: 'Start Timer',
          viewProjects: 'View Projects',
          approvals: 'Pending Approvals',
          reports: 'View Reports',
        },
        weeklyHours: {
          title: 'This Week',
        },
        attention: {
          title: 'Needs Attention',
          allGood: 'All Clear!',
          noIssues: 'All projects are running smoothly.',
        },
        teamWorkload: {
          title: 'Team Workload',
        },
        // Manager Dashboard
        managerSubtitle: 'Your projects are moving forward. Here is the overview.',
        managedProjects: 'Managed Projects',
        projectsAtRisk: 'Projects At Risk',
        teamAvailability: 'Team Availability',
        needsAttentionSuffix: 'Needs Attention',
        criticalSuffix: 'Critical',
        onlineSuffix: 'Online',
        projectsNeedingAttention: 'Projects Needing Attention',
        teamStatus: 'Team Status',
        online: 'Online',
        sickLeave: 'Sick Leave',
        away: 'Away',
        allOnTrack: 'All projects are on track! 🎉',
        deadlinePassed: 'Deadline passed on {{date}}',
        criticalInfrastructure: 'Critical Infrastructure',
        sitesHackedDown: '{{count}} sites are Hacked or Down!',
        // Developer Dashboard
        developerSubtitle: 'Ready to build something amazing today?',
        hoursToday: 'Hours Today',
        assignedTasks: 'Assigned Tasks',
        highPriority: '{{count}} high priority',
        myActiveProjects: 'My Active Projects',
        noActiveProjects: 'No active projects found',
        // Widget strings
        myTasks: 'My Tasks',
        noPendingTasks: 'No pending tasks assigned to you',
        pendingApprovals: 'Pending Approvals',
        reviewAll: 'Review All',
        allCaughtUp: 'All caught up! No pending approvals.',
        new: 'New',
        week: 'Week',
      },

      // Projects
      projects: {
        title: 'All Projects',
        newProject: 'New Project',
        editProject: 'Edit Project',
        createProject: 'Create Project',
        deleteProject: 'Delete Project',
        noProjects: 'No projects found',
        projectDetails: 'Project Details',
        stats: {
          down: 'Down',
          hacked: 'Hacked',
          atRisk: 'At Risk',
          monitoring: 'Monitoring',
          downTooltip: 'Sites currently down',
          hackedTooltip: 'Hacked websites - CRITICAL',
          atRiskTooltip: 'Compromised or At Risk',
          monitoringTooltip: 'Need attention',
        },
        filters: {
          allHealth: 'All Health',
          allSecurity: 'All Security',
          allManagers: 'All Managers',
          allDevelopers: 'All Developers',
          allTags: 'All Tags',
        },
        table: {
          project: 'Project',
          pm: 'PM',
          team: 'Team',
          tags: 'Tags',
          todos: 'TODOs',
          health: 'Health',
          security: 'Security',
          actions: 'Actions',
        },
        form: {
          name: 'Project Name',
          namePlaceholder: 'Enter project name',
          url: 'Website URL',
          urlPlaceholder: 'https://example.com',
          externalId: 'External ID',
          externalIdPlaceholder: 'External system ID',
          clientEmail: 'Client Email',
          clientEmailPlaceholder: 'client@example.com',
          clientPhone: 'Client Phone',
          manager: 'Project Manager',
          developer: 'Primary Developer',
          developers: 'Team Members',
          selectManager: 'Select manager',
          selectDeveloper: 'Select developer',
          selectDevelopers: 'Select team members',
          tags: 'Tags',
          selectTags: 'Select tags',
          notes: 'Notes',
          notesPlaceholder: 'Additional notes about this project',
          healthStatus: 'Health Status',
          securityStatus: 'Security Status',
          addMaintenanceTodos: 'Add standard maintenance todos',
        },
        health: {
          online: 'Online',
          warning: 'Warning',
          down_error: 'Down/Error',
          confirming_down: 'Checking...',
          maintenance: 'Maintenance',
          unknown: 'Unknown',
        },
        uptime: {
          noDataYet: 'No data yet',
          noDataHint: 'Statistics will appear after the first automatic checks',
          checkedAutomatically: 'Checked automatically',
          statsError: 'Could not load uptime statistics',
          apiKeyConfiguredPlaceholder: '••••••••  (configured — enter a new key to replace)',
        },
        security: {
          secure: 'Secure',
          monitoring: 'Monitoring',
          compromised: 'At Risk',
          hacked: 'Hacked',
        },
        tabs: {
          todos: 'Todos',
          resources: 'Resources',
          health: 'Health',
          reports: 'Reports',
          credentials: 'Credentials',
        },
        messages: {
          created: 'Project created successfully',
          updated: 'Project updated successfully',
          deleted: 'Project deleted successfully',
          createError: 'Failed to create project',
          updateError: 'Failed to update project',
          deleteError: 'Failed to delete project',
        },
      },

      // Todos
      todos: {
        title: 'Todos',
        addTodo: 'Add Todo',
        editTodo: 'Edit Todo',
        deleteTodo: 'Delete Todo',
        noTodos: 'No todos yet',
        task: 'Task',
        dueDate: 'Due Date',
        form: {
          title: 'Title',
          titlePlaceholder: 'What needs to be done?',
          description: 'Description',
          descriptionPlaceholder: 'Additional details...',
          priority: 'Priority',
          status: 'Status',
          dueDate: 'Due Date',
          assignedTo: 'Assigned To',
          selectAssignee: 'Select assignee',
        },
        priority: {
          low: 'Low',
          medium: 'Medium',
          high: 'High',
          urgent: 'Urgent',
        },
        status: {
          pending: 'Pending',
          in_progress: 'In Progress',
          completed: 'Completed',
          cancelled: 'Cancelled',
        },
        messages: {
          created: 'Todo created successfully',
          updated: 'Todo updated successfully',
          deleted: 'Todo deleted successfully',
        },
      },

      // Resources
      resources: {
        title: 'Resources',
        addResource: 'Add Resource',
        editResource: 'Edit Resource',
        deleteResource: 'Delete Resource',
        noResources: 'No resources yet',
        form: {
          title: 'Title',
          titlePlaceholder: 'Resource title',
          type: 'Type',
          url: 'URL',
          urlPlaceholder: 'https://...',
          file: 'File',
          selectFile: 'Select file',
          isQuickAction: 'Quick Action',
          isQuickActionHint: 'Show in quick actions',
        },
        type: {
          file: 'File',
          link: 'Link',
        },
        messages: {
          created: 'Resource added successfully',
          updated: 'Resource updated successfully',
          deleted: 'Resource deleted successfully',
          downloadError: 'Failed to download file',
        },
      },

      // Credentials / Vault
      vault: {
        title: 'Vault',
        subtitle: 'Secure password management',
        addCredential: 'Add Credential',
        addNewCredential: 'Add New Credential',
        editCredential: 'Edit Credential',
        deleteCredential: 'Delete Credential',
        deleteConfirm: 'Are you sure you want to delete this credential?',
        noCredentials: 'No credentials stored',
        showPassword: 'Show Password',
        hidePassword: 'Hide Password',
        copyPassword: 'Copy Password',
        copied: 'Copied!',
        share: 'Share',
        createShare: 'Create Share Link',
        noPassword: 'No password',
        open: 'Open',
        searchPlaceholder: 'Search credentials...',
        filterByType: 'Filter by type',
        table: {
          title: 'Title',
          type: 'Type',
          username: 'Username',
          password: 'Password',
          url: 'URL',
          actions: 'Actions',
        },
        types: {
          all: 'All Types',
          wordpress: 'WordPress',
          ssh: 'SSH',
          ftp: 'FTP',
          database: 'Database',
          hosting: 'Hosting',
          email: 'Email',
          api: 'API',
          apiKey: 'API Key',
          other: 'Other',
        },
        sort: {
          recentlyUpdated: 'Recently Updated',
          title: 'Title',
          project: 'Project',
        },
        form: {
          label: 'Label',
          labelPlaceholder: 'e.g., Admin Login',
          project: 'Project',
          selectProject: 'Select Project',
          selectType: 'Select Type',
          title: 'Title',
          titlePlaceholder: 'Credential Title',
          titleHelp: "e.g., 'Production Server', 'Main DB User'",
          username: 'Username',
          usernameLogin: 'Username / Login',
          usernamePlaceholder: 'Username or email',
          password: 'Password',
          passwordApiKey: 'Password / API Key',
          passwordPlaceholder: 'Enter password',
          apiKeyPlaceholder: 'Enter API key',
          leaveBlankToKeep: 'Leave blank to keep current',
          enterNewPassword: 'Enter new password',
          url: 'URL',
          loginUrl: 'Login URL (Optional)',
          urlPlaceholder: 'https://...',
          hostnameIp: 'Hostname / IP',
          hostnamePlaceholder: 'e.g. 192.168.1.1 or db.example.com',
          port: 'Port',
          databaseName: 'Database Name',
          notes: 'Notes',
          notesPlaceholder: 'Any additional instructions...',
          category: 'Category',
          selectCategory: 'Select category',
          selectRequired: 'Please select a project',
          typeRequired: 'Please select type',
        },
        shareModal: {
          title: 'Share Credential',
          description: 'Generate a secure, time-limited link to share',
          linkExpiration: 'Link Expiration',
          hour1: '1 Hour',
          hours24: '24 Hours',
          days3: '3 Days',
          days7: '7 Days',
          maxViews: 'Maximum Views',
          oneView: '1 View',
          custom: 'Custom',
          views: 'Views',
          passwordProtected: 'Password Protected',
          noPasswordToggle: 'No Password',
          accessPassword: 'Access Password',
          accessPasswordHelp: 'Recipient must enter this password to view the credential.',
          setPassword: 'Set a shared password',
          passwordRequired: 'Please set a password',
          recipientEmail: 'Recipient Email (Optional)',
          recipientPlaceholder: 'recipient@example.com',
          generateLink: 'Generate Link',
          linkCreated: 'Share Link Created!',
          linkExpires: 'This link will expire on',
          copy: 'Copy',
          linkCopied: 'Link copied to clipboard',
          securityNote: 'Security Note',
          securityWarning: 'Anyone with this link can access the credential. Share it securely.',
          close: 'Close',
          success: 'Secure link generated!',
          error: 'Failed to generate link. Please try again.',
        },
        categories: {
          hosting: 'Hosting',
          cms: 'CMS',
          ftp: 'FTP',
          database: 'Database',
          email: 'Email',
          other: 'Other',
        },
        messages: {
          created: 'Credential added successfully',
          updated: 'Credential updated successfully',
          deleted: 'Credential deleted successfully',
          revealError: 'Failed to reveal password',
          createError: 'Failed to add credential',
          updateError: 'Failed to update credential',
        },
      },

      // Library
      library: {
        title: 'Library Resources',
        subtitle: 'Shared files that can be linked to multiple projects',
        uploadFile: 'Upload File',
        uploadFirstFile: 'Upload First File',
        uploadToLibrary: 'Upload to Library',
        editResource: 'Edit Library Resource',
        noResources: 'No library resources yet',
        selectFile: 'Select File',
        selectNewFile: 'Select New File',
        replaceFile: 'Replace File (optional)',
        file: 'File',
        current: 'Current',
        tip: 'Go to any project\'s Resources tab and click "Link from Library" to use these files.',
        tipLabel: 'Tip:',
        clickToDownload: 'Click to download',
        selectFileError: 'Please select a file to upload',
        table: {
          title: 'Title',
          category: 'Category',
          file: 'File',
          usedIn: 'Used In',
        },
        stats: {
          totalFiles: 'Total Files',
          inUse: 'In Use',
          categories: 'Categories',
        },
        form: {
          title: 'Title',
          titlePlaceholder: 'e.g. Developer Guide, Security Checklist',
          titleRequired: 'Please enter a title',
          category: 'Category',
          categoryPlaceholder: 'Select or type a category',
          notes: 'Notes',
          notesPlaceholder: 'Optional notes about this resource...',
        },
        deleteConfirm: {
          title: 'Delete Library Resource?',
          linkedWarning: 'This file is linked to {{count}} project(s). It will be unlinked from all of them.',
          noUndo: 'This action cannot be undone.',
        },
        messages: {
          uploaded: 'Library resource uploaded',
          uploadError: 'Failed to upload resource',
          updated: 'Library resource updated',
          updateError: 'Failed to update resource',
          deleted: 'Library resource deleted',
          deleteError: 'Failed to delete resource',
          downloadError: 'Failed to download file',
        },
      },

      // Support
      support: {
        subtitle: 'Ticket management & client communication',
        comingSoon: 'Coming Soon',
        comingSoonDescription: 'The support ticket system is currently under development. Stay tuned — this feature will be available soon!',
      },

      // Team
      team: {
        title: 'Team',
        subtitle: 'Manage team members',
        addMember: 'Add Team Member',
        editMember: 'Edit Member',
        deleteMember: 'Delete Member',
        noMembers: 'No team members found',
        viewProjects: 'View Projects',
        searchPlaceholder: 'Search team...',
        filterByRole: 'Filter by role',
        filterByTag: 'Filter by tag',
        filters: {
          allRoles: 'All Roles',
          allTags: 'All Tags',
        },
        table: {
          member: 'Member',
          user: 'User',
          role: 'Role',
          email: 'Email',
          phone: 'Phone',
          tags: 'Tags',
          projects: 'Projects',
          hourlyRate: 'Hourly Rate',
          status: 'Status',
          actions: 'Actions',
          adminAccess: 'Admin Access',
        },
        form: {
          name: 'Full Name',
          namePlaceholder: 'Enter full name',
          email: 'Email',
          emailPlaceholder: 'email@example.com',
          phone: 'Phone',
          phonePlaceholder: '+49 123 456 789',
          role: 'Role',
          selectRole: 'Select role',
          password: 'Password',
          passwordPlaceholder: 'Enter password',
          passwordHint: 'Leave blank to keep current password',
          newPassword: 'New Password',
          newPasswordPlaceholder: 'Enter new password',
          tags: 'Tags',
          selectTags: 'Select tags',
          hourlyRate: 'Hourly Rate',
          hourlyRatePlaceholder: 'e.g., 50.00',
          adminAccess: 'Admin Access',
          adminAccessHint: 'Grant this user full admin-level permissions while keeping their role',
          generatePassword: 'Generate Password',
          passwordGenerated: 'Password generated',
        },
        passwordRules: {
          minLength: 'At least 8 characters',
          uppercase: 'At least one uppercase letter',
          lowercase: 'At least one lowercase letter',
          number: 'At least one number',
          special: 'At least one special character (!@#$%^&*)',
        },
        roles: {
          admin: 'Admin',
          manager: 'Manager',
          developer: 'Developer',
        },
        messages: {
          created: 'Team member added successfully',
          updated: 'Team member updated successfully',
          deleted: 'Team member deleted successfully',
          passwordReset: 'Password reset successfully',
          passwordResetError: 'Failed to reset password',
          resetLinkSent: 'Password reset link sent',
          resetLinkError: 'Failed to send reset link',
        },
        resetPassword: 'Reset Password',
        resetPasswordTitle: 'Reset Password',
        resetPasswordDesc: 'Set a new password for {{name}}',
        sendResetLink: 'Send Reset Link',
        sendResetLinkConfirm: 'Send a password reset link to {{email}}?',
      },

      // Tags
      tags: {
        title: 'Tags',
        subtitle: 'Organize projects and team members',
        addTag: 'Add Tag',
        createTag: 'Create Tag',
        editTag: 'Edit Tag',
        deleteTag: 'Delete Tag',
        noTags: 'No tags created yet',
        newTag: 'New Tag',
        table: {
          tag: 'Tag',
          slug: 'Slug',
          projects: 'Projects',
          projectCount: '{{count}} projects',
          actions: 'Actions',
        },
        form: {
          name: 'Tag Name',
          namePlaceholder: 'Enter tag name',
          nameRequired: 'Please enter tag name',
          slug: 'Slug',
          slugPlaceholder: 'auto-generated',
          color: 'Color',
          selectColor: 'Select color',
        },
        colors: {
          default: 'Default',
          blue: 'Blue',
          green: 'Green',
          red: 'Red',
          orange: 'Orange',
          purple: 'Purple',
          cyan: 'Cyan',
          magenta: 'Magenta',
          gold: 'Gold',
        },
        messages: {
          created: 'Tag created successfully',
          updated: 'Tag updated successfully',
          deleted: 'Tag deleted successfully',
        },
      },

      // Activity Log
      activity: {
        title: 'Activity Log',
        subtitle: 'Audit trail of all system activities',
        searchPlaceholder: 'Search activities...',
        subjectType: 'Subject Type',
        noActivity: 'No activity yet',
        loadError: 'Failed to load activity',
        table: {
          user: 'User',
          action: 'Action',
          subject: 'Subject',
          time: 'Time',
        },
        types: {
          all: 'All Types',
          project: 'Project',
          credential: 'Credential',
          todo: 'Todo',
          user: 'User',
          unknown: 'Unknown',
          system: 'System',
        },
      },

      // Settings
      settings: {
        title: 'Settings',
        subtitle: 'Configure global application settings. Changes apply immediately.',
        accessDenied: 'Access Denied',
        noPermission: "You don't have permission to access this page.",
        loadError: 'Failed to load settings',
        loadErrorHint: 'Please try refreshing the page.',
        saveSuccess: 'Settings saved successfully',
        saveError: 'Failed to save settings',
        reset: 'Reset',
        saveSettings: 'Save Settings',
        uptime: {
          title: 'Uptime Monitoring',
          enabled: 'Automatic Monitoring',
          enabledTooltip: 'Enable or disable automatic uptime checks for all projects',
          enabledOn: 'Enabled',
          enabledOff: 'Disabled',
          interval: 'Check Interval',
          intervalTooltip: 'How often to check sites (in minutes)',
          intervalOptions: {
            min1: 'Every 1 minute',
            min2: 'Every 2 minutes',
            min3: 'Every 3 minutes',
            min5: 'Every 5 minutes (Recommended)',
            min10: 'Every 10 minutes',
            min15: 'Every 15 minutes',
            min30: 'Every 30 minutes',
          },
          concurrency: 'Concurrency',
          concurrencyTooltip: 'Number of sites to check simultaneously',
          concurrencyUnit: 'sites at once',
          timeout: 'Request Timeout',
          timeoutTooltip: 'How long to wait for a response before marking site as down',
          timeoutUnit: 'seconds',
          tip: 'Tip: For ~200 sites with concurrency of 15 and 5-minute interval, checks complete in ~2-3 minutes.',
        },
        backup: {
          title: 'Backup Settings',
          storageLocation: 'Storage Location',
          active: 'Active',
          storageDriverInfo: 'Storage driver is configured via environment variables (.env). Change BACKUP_DRIVER to switch.',
          defaultFrequency: 'Default Frequency',
          frequencyTooltip: 'Default backup frequency for new projects',
          daily: 'Daily',
          weekly: 'Weekly',
          monthly: 'Monthly',
          retentionPeriod: 'Retention Period',
          retentionTooltip: 'How long to keep backups before automatic deletion',
          retentionUnit: 'days',
          scheduledBackups: 'Scheduled Backups',
          frequency: 'Frequency',
          time: 'Time',
          retentionPolicy: 'Retention Policy',
          maxBackups: 'Max Backups',
          maxAge: 'Max Age',
          minKeep: 'Min Keep',
          defaultContents: 'Default Backup Contents',
          database: 'Database',
          files: 'Files',
          uploads: 'Uploads',
        },
        drivers: {
          local: 'Local Storage (Server)',
          s3: 'Amazon S3',
          gcs: 'Google Cloud Storage',
          gdrive: 'Google Drive',
        },
      },

      // GDPR Audit
      gdpr: {
        title: 'GDPR Audit',
        subtitle: 'Headless browser scan for cookies, trackers & consent compliance',
        quick: 'Quick',
        full: 'Full',
        runQuickAudit: 'Run Quick Audit',
        runFullAudit: 'Run Full Audit',
        rerunQuick: 'Re-run Quick Audit',
        rerunFull: 'Re-run Full Audit',
        pdf: 'PDF',
        viewPdf: 'View PDF',
        saveToReports: 'Save to Reports',
        reportSaved: 'Report saved successfully',
        reportSaveError: 'Failed to save report',
        running: {
          title: 'Running {{mode}} audit...',
          subtitle: 'Headless browser is scanning the website. This may take up to 60 seconds.',
        },
        noAudit: {
          title: 'No GDPR Audit Yet',
          description: 'Run a Quick or Full audit to analyze tracking, cookies, and consent compliance.',
        },
        error: {
          title: 'Audit Failed',
          retry: 'Retry Audit',
        },
        score: {
          criticalIssues: 'Critical Issues',
          needsWork: 'Needs Work',
          good: 'Good',
          excellent: 'Excellent',
        },
        stats: {
          trackers: 'Trackers',
          trackingCookies: 'Tracking Cookies',
          banner: 'Banner',
          detected: 'Detected',
          notFound: 'Not Found',
          acceptFlow: 'Accept Flow',
          rejectFlow: 'Reject Flow',
          working: 'Working',
          noActivity: 'No activity',
          clean: 'Clean',
          leaking: 'Leaking!',
        },
        badges: {
          fullAudit: 'Full Audit',
          quickScan: 'Quick Scan',
          aiEnhanced: 'AI-Enhanced',
          basicScan: 'Basic Scan',
        },
        ai: {
          title: 'AI Compliance Analysis',
          violations: 'Violations',
          whatsRight: "What's Done Right",
          recommendations: 'Recommendations',
          severity: 'Severity',
          issue: 'Issue',
          details: 'Details',
          legalRef: 'Legal Ref',
          fix: 'Fix',
        },
        sections: {
          auditChecks: 'Audit Checks',
          cookieBanner: 'Cookie Banner Analysis',
          trackingServices: 'Tracking Services (pre-consent)',
          allCookies: 'All Cookies',
          acceptAllFlow: 'Accept-All Flow',
          rejectFlow: 'Reject Flow',
        },
        table: {
          check: 'Check',
          status: 'Status',
          details: 'Details',
          service: 'Service',
          requests: 'Requests',
          name: 'Name',
          value: 'Value',
          domain: 'Domain',
          tracking: 'Tracking',
        },
        bannerLabels: {
          detected: 'Detected',
          solution: 'Solution',
          hasRejectOption: 'Has Reject Option',
          preTickedCheckboxes: 'Pre-ticked Checkboxes',
        },
        metadata: {
          mode: 'Mode',
          scannedAt: 'Scanned',
          duration: 'Duration',
          seconds: '{{count}}s',
        },
      },

      // Accessibility Audit
      accessibility: {
        title: 'Accessibility Audit',
        subtitle: 'WCAG 2.1 Level AA compliance check',
        noUrl: 'No website URL configured',
        noUrlHint: 'Add a URL to enable accessibility auditing',
        runAudit: 'Run Audit',
        rerunAudit: 'Re-run Audit',
        pdf: 'PDF',
        viewPdf: 'View PDF',
        saveToReports: 'Save to Reports',
        reportSaved: 'Report saved successfully',
        reportSaveError: 'Failed to save report',
        auditComplete: 'Accessibility audit completed!',
        auditFailed: 'Failed to run accessibility audit',
        lastAudit: 'Last audited',
        running: {
          title: 'Running Accessibility Audit...',
          subtitle: 'Scanning for WCAG 2.1 violations — this may take 15-20 seconds',
        },
        noAudit: {
          title: 'No accessibility audit yet',
          description: 'Run an audit to check this website for WCAG 2.1 Level AA accessibility violations',
        },
        score: {
          accessible: 'Accessible',
          needsWork: 'Needs Improvement',
          criticalIssues: 'Critical Issues',
        },
        stats: {
          violations: 'Violations',
          critical: 'Critical',
          serious: 'Serious',
          passed: 'Passed',
        },
        badges: {
          aiEnhanced: 'AI-Enhanced',
          automatedScan: 'Automated Scan',
        },
        ai: {
          title: 'AI Accessibility Analysis',
          violations: 'Violations',
          whatsRight: "What's Done Right",
          recommendations: 'Recommendations',
          impact: 'Impact',
          issue: 'Issue',
          details: 'Details',
          wcag: 'WCAG',
          fix: 'Fix',
        },
        sections: {
          violationsByCategory: 'Violations by Category',
          customChecks: 'Additional Checks',
          passed: 'Passed Checks',
          needsReview: 'Needs Manual Review',
        },
        table: {
          rule: 'Rule',
          elements: 'Elements',
          example: 'Example',
          check: 'Check',
          details: 'Details',
          category: 'Category',
        },
      },

      // Time Tracking
      time: {
        title: 'My Time',
        subtitle: 'Track your work hours',
        today: 'Today',
        thisWeek: 'This Week',
        thisMonth: 'This Month',
        total: 'Total',
        hours: 'hours',
        minutes: 'minutes',
        startTimer: 'Start Timer',
        stopTimer: 'Stop Timer',
        addEntry: 'Add Entry',
        editEntry: 'Edit Time Entry',
        addTimeEntry: 'Add Time Entry',
        deleteEntry: 'Delete Entry',
        noEntries: 'No time entries yet',
        // View labels
        week: 'Week',
        month: 'Month',
        all: 'All',
        from: 'From',
        to: 'To',
        // Stat cards
        todaysTotal: "Today's Total",
        weekTotal: 'Week {{week}} Total',
        monthTotal: '{{month}} Total',
        allTimeTotal: 'All Time Total',
        entries: 'Entries',
        draftUnsubmitted: 'Draft (Unsubmitted)',
        entriesCount: '({{count}} entries)',
        // Table headers
        table: {
          project: 'Project',
          description: 'Description',
          time: 'Time',
          duration: 'Duration',
          status: 'Status',
          actions: 'Actions',
          noDescription: 'No description',
        },
        // Selection bar
        selection: {
          selected: '{{count}} draft entry selected ({{duration}})',
          selected_plural: '{{count}} draft entries selected ({{duration}})',
          clearSelection: 'Clear Selection',
          submitSelected: 'Submit Selected for Approval',
          selectAllDraft: 'Select All Draft',
          submitSuccess: 'Entries submitted for approval',
          submitError: 'Failed to submit entries',
        },
        // Empty states
        empty: {
          today: 'No entries today. Start the timer!',
          all: 'No entries found. Use date range to filter.',
          period: 'No entries for this {{period}}.',
        },
        // Delete confirm
        deleteConfirm: 'Delete this entry?',
        // Pagination
        totalEntries: '{{total}} entries',
        // Modal buttons
        cancel: 'Cancel',
        update: 'Update',
        form: {
          project: 'Project',
          selectProject: 'Select project...',
          projectRequired: 'Please select a project',
          description: 'Description',
          descriptionPlaceholder: 'What did you work on?',
          date: 'Date',
          startTime: 'Start Time',
          endTime: 'End Time',
          duration: 'Duration',
          billable: 'Billable',
        },
        messages: {
          started: 'Timer started',
          stopped: 'Timer stopped',
          created: 'Time entry added',
          updated: 'Time entry updated',
          deleted: 'Time entry deleted',
        },
      },

      // Approvals
      approvals: {
        title: 'Approvals',
        subtitle: 'Review and approve timesheets',
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        approve: 'Approve',
        reject: 'Reject',
        approveAll: 'Approve All',
        noApprovals: 'No pending approvals',
        timesheet: 'Timesheet',
        submittedBy: 'Submitted by',
        submittedAt: 'Submitted at',
        totalHours: 'Total Hours',
        // Table headers
        table: {
          developer: 'Developer',
          week: 'Week',
          totalTime: 'Total Time',
          entries: 'Entries',
          submitted: 'Submitted',
          actions: 'Actions',
          review: 'Review',
        },
        // Pending banner
        awaitingApproval: 'timesheets awaiting your approval',
        // Details modal
        details: {
          timesheetTitle: "{{name}}'s Timesheet",
          totalTime: 'Total Time',
          entries: 'Entries',
          defaultRate: 'Default Rate',
          perHour: '{{rate}}/hr',
          project: 'Project',
          description: 'Description',
          noDescription: 'No description',
          date: 'Date',
          duration: 'Duration',
          ratePerHour: 'Rate ($/hr)',
          close: 'Close',
        },
        // Selection info
        selection: {
          selected: 'Selected: {{count}} entries',
          approveSelected: 'Approve Selected ({{count}})',
          rejectSelected: 'Reject Selected ({{count}})',
          selectWarning: 'Please select at least one entry',
        },
        // Reject modal
        rejectModal: {
          title: 'Reject Selected Entries',
          description: 'Please provide a reason for rejecting {{count}} entries. The developer will be notified.',
          placeholder: 'Reason for rejection...',
          reasonRequired: 'Please provide a reason for rejection',
          okText: 'Reject {{count}} Entries',
        },
        messages: {
          approved: 'Selected entries approved!',
          rejected: 'Selected entries rejected',
          approveError: 'Failed to approve',
          rejectError: 'Failed to reject',
          invoiceCreated: 'Invoice #{{number}} created',
        },
      },

      // Invoices
      invoices: {
        title: 'Invoices',
        subtitle: 'Manage billing and payments',
        newInvoice: 'New Invoice',
        noInvoices: 'No invoices found',
        invoice: 'Invoice',
        invoiceNumber: 'Invoice #',
        amount: 'Amount',
        status: {
          draft: 'Draft',
          pending: 'Pending',
          approved: 'Approved',
          paid: 'Paid',
          declined: 'Declined',
        },
        table: {
          invoiceNumber: 'Invoice #',
          developer: 'Developer',
          period: 'Period',
          hours: 'Hours',
          amount: 'Amount',
          status: 'Status',
          actions: 'Actions',
          view: 'View',
          pay: 'Pay',
        },
        filters: {
          from: 'From',
          to: 'To',
          allDevelopers: 'All Developers',
          allStatus: 'All Status',
          thisMonth: 'This Month',
          lastMonth: 'Last Month',
          thisYear: 'This Year',
        },
        stats: {
          totalInvoiced: 'Total Invoiced',
          pendingPayment: 'Pending Payment',
          paid: 'Paid',
        },
        details: {
          title: 'Invoice Details',
          developer: 'Developer',
          period: 'Period',
          totalHours: 'Total Hours',
          total: 'Total',
          close: 'Close',
          timeEntries: 'Time Entries',
          project: 'Project',
          description: 'Description',
          duration: 'Duration',
          noProject: 'No project',
          noEntries: 'No entries',
        },
        actions: {
          approve: 'Approve',
          decline: 'Decline',
          markPaid: 'Mark as Paid',
          download: 'Download PDF',
          exportCsv: 'Export CSV',
        },
        messages: {
          approved: 'Invoice approved!',
          declined: 'Invoice declined',
          paid: 'Invoice marked as paid!',
          approveError: 'Failed to approve',
          declineError: 'Failed to decline',
          paidError: 'Failed to mark as paid',
          exported: 'Invoices exported to CSV',
          noExport: 'No invoices to export',
        },
        apiError: 'Invoice API Not Available',
        apiErrorDesc: 'The invoice API module could not be loaded. Please restart the development server.',
      },

      // Analytics
      analytics: {
        title: 'Time Analytics',
        subtitle: 'Insights and trends from your team\'s time tracking',
        totalHours: 'Total Hours',
        totalEntries: 'Total Entries',
        activeProjects: 'Active Projects',
        avgHoursDay: 'Avg Hours/Day',
        weeklyTrend: 'Weekly Trend',
        timeByProject: 'Time by Project',
        teamLeaderboard: 'Team Leaderboard',
        projectDistribution: 'Project Distribution',
        noData: 'No data for this period',
        hours: 'Hours',
        table: {
          developer: 'Developer',
          hours: 'Hours',
          entries: 'Entries',
          avgPerEntry: 'Avg/Entry',
        },
        presets: {
          last7: 'Last 7 days',
          last30: 'Last 30 days',
          thisMonth: 'This Month',
          lastMonth: 'Last Month',
        },
      },

      // Financial Reports
      reports: {
        title: 'Financial Reports',
        subtitle: 'View approved time entries and process payments',
        addReport: 'Add Report',
        editReport: 'Edit Report',
        deleteReport: 'Delete Report',
        noReports: 'No maintenance reports yet',
        downloadPdf: 'Download PDF',
        form: {
          date: 'Report Date',
          duration: 'Duration (minutes)',
          durationPlaceholder: 'e.g., 60',
          summary: 'Summary',
          summaryPlaceholder: 'Brief summary of work performed',
          issuesFound: 'Issues Found',
          issuesResolved: 'Issues Resolved',
          addIssue: 'Add Issue',
        },
        messages: {
          created: 'Report created successfully',
          updated: 'Report updated successfully',
          deleted: 'Report deleted successfully',
        },
      },

      // Availability
      availability: {
        title: 'Set Availability',
        setStatus: 'Set Status',
        setStatusFor: 'Set Status for',
        editAbsence: 'Edit Absence',
        cancelAbsence: 'Cancel Absence',
        selectMember: 'Team Member',
        selectMemberPlaceholder: 'Leave empty to set for yourself',
        statusLabel: 'Status',
        duration: 'Duration',
        reason: 'Reason (Optional)',
        statusSet: 'Availability status set successfully',
        updated: 'Availability updated successfully',
        cancelled: 'Absence cancelled successfully',
        error: 'Failed to update availability',
        confirmCancel: 'Are you sure you want to cancel this absence?',
        setByAdmin: 'Set by {{name}}',
        absenceIndicator: '{{status}} until {{date}}',
        absenceNoEnd: '{{status}} — no end date',
        status: {
          available: 'Available',
          sick: 'Sick Leave',
          vacation: 'Vacation',
          parental: 'Parental Leave',
          remote: 'Remote',
          half_day: 'Half Day',
          other: 'Other',
        },
        form: {
          status: 'Status',
          note: 'Note',
          notePlaceholder: 'Optional note...',
          until: 'Until',
        },
        messages: {
          updated: 'Availability updated',
        },
      },

      // Theme
      theme: {
        light: 'Light',
        dark: 'Dark',
        system: 'System',
      },

      // Language
      language: {
        en: 'English',
        de: 'Deutsch',
      },

      // Errors
      errors: {
        generic: 'Something went wrong',
        notFound: 'Not found',
        unauthorized: 'Unauthorized',
        forbidden: 'Access denied',
        network: 'Network error',
        validation: 'Validation error',
      },

      // Integration Tokens
      integrationTokens: {
        card: {
          title: 'API & Integrations',
          createButton: 'Create Token',
          description:
            'Long-lived tokens for AI clients over MCP. Each token is limited to the scopes you select — and never to more than your role already allows.',
          revokeAllNotice:
            'Signing out everywhere, or changing your password, also revokes all your integration tokens.',
        },
        table: {
          name: 'Name',
          scopes: 'Permissions',
          expiry: 'Validity',
          lastUsed: 'Last Used',
        },
        empty: 'No integration tokens yet',
        neverUsed: 'Never used',
        neverExpires: 'Never expires',
        expired: 'Expired',
        expiresIn: 'expires {{relative}}',
        revoke: 'Revoke',
        revokeConfirm: {
          title: 'Revoke "{{name}}"?',
          content: 'Any client using this token will lose access immediately. This cannot be undone.',
          okText: 'Revoke',
          cancelText: 'Cancel',
        },
        toasts: {
          revoked: 'Token revoked',
          revokeError: 'Failed to revoke token',
          createError: 'Could not create token. Is the password correct?',
          copied: 'Copied to clipboard',
        },
        scopeTags: {
          read: 'Read',
          write: 'Write',
          wp: 'WordPress',
          wpDestructive: 'WP critical',
        },
        scopes: {
          read: {
            label: 'Read',
            hint: 'View projects, todos, time entries, and team. Changes nothing.',
          },
          write: {
            label: 'Write',
            hint: 'Create and update todos, time entries, and project data.',
          },
          wp: {
            label: 'WordPress',
            hint: 'Maintenance mode, cache, updates, and backups on client sites. Reversible.',
          },
          wpDestructive: {
            label: 'WordPress — Critical',
            hint: 'Emergency recovery, backup restore, and bulk actions across all sites. Not reversible.',
          },
        },
        expiryOptions: {
          days30: '30 Days',
          days90: '90 Days',
          year1: '1 Year',
          never: 'Never expires',
        },
        create: {
          title: 'Create New Integration Token',
          createdTitle: 'Token Created',
          nameLabel: 'Name',
          namePlaceholder: 'Claude Code — MacBook',
          nameRequired: 'Please provide a name',
          nameHelp: 'What is this token for? e.g. "Claude Code — MacBook"',
          scopesLabel: 'Permissions',
          scopesRequired: 'Select at least one permission',
          scopeUnavailable: ' — not available for your role',
          expiryLabel: 'Validity',
          passwordLabel: 'Current Password',
          passwordRequired: 'Enter your password to confirm',
          passwordHelp: 'To confirm this is really you.',
          cancel: 'Cancel',
          submit: 'Create Token',
          done: "Done — I've saved the token",
          revealWarningTitle: 'This token is shown only once.',
          revealWarningDescription:
            "Copy it now. After that it can't be shown again — only revoked and re-created.",
          tokenLabel: 'Token',
          copyToken: 'Copy Token',
          connectClient: 'Connect Client',
          connectInstructions: 'Run this command in your terminal:',
          copyCommand: 'Copy Command',
        },
      },
    },
  },
  de: {
    translation: {
      // Common
      common: {
        loading: 'Laden...',
        error: 'Fehler',
        success: 'Erfolg',
        cancel: 'Abbrechen',
        save: 'Speichern',
        create: 'Erstellen',
        update: 'Aktualisieren',
        delete: 'Löschen',
        edit: 'Bearbeiten',
        close: 'Schließen',
        search: 'Projekte suchen...',
        filter: 'Filtern',
        refresh: 'Aktualisieren',
        submit: 'Absenden',
        back: 'Zurück',
        next: 'Weiter',
        yes: 'Ja',
        no: 'Nein',
        view: 'Anzeigen',
        download: 'Herunterladen',
        upload: 'Hochladen',
        add: 'Hinzufügen',
        remove: 'Entfernen',
        clear: 'Leeren',
        apply: 'Anwenden',
        reset: 'Zurücksetzen',
        actions: 'Aktionen',
        status: 'Status',
        type: 'Typ',
        name: 'Name',
        email: 'E-Mail',
        phone: 'Telefon',
        date: 'Datum',
        time: 'Zeit',
        description: 'Beschreibung',
        notes: 'Notizen',
        priority: 'Priorität',
        assignedTo: 'Zugewiesen an',
        createdAt: 'Erstellt am',
        updatedAt: 'Aktualisiert am',
        total: 'gesamt',
        all: 'Alle',
        none: 'Keine',
        notifications: 'Benachrichtigungen',
        noNotifications: 'Keine neuen Benachrichtigungen',
        viewAll: 'Alle anzeigen',
        noData: 'Keine Daten',
        confirmDelete: 'Möchten Sie dies wirklich löschen?',
        deleteSuccess: 'Erfolgreich gelöscht',
        deleteError: 'Löschen fehlgeschlagen',
        saveSuccess: 'Erfolgreich gespeichert',
        saveError: 'Speichern fehlgeschlagen',
        required: 'Erforderlich',
        unknownProject: 'Unbekanntes Projekt',
        optional: 'Optional',
      },
      
      // Navigation
      nav: {
        dashboard: 'Dashboard',
        projects: 'Projekte',
        vault: 'Tresor',
        myTime: 'Meine Zeit',
        team: 'Team',
        tags: 'Tags',
        activityLog: 'Aktivitätsprotokoll',
        approvals: 'Genehmigungen',
        reports: 'Berichte',
        analytics: 'Analytik',
        invoices: 'Rechnungen',
        profile: 'Profil',
        logout: 'Abmelden',
        main: 'Hauptmenü',
        management: 'Verwaltung',
        timeTracking: 'Zeiterfassung',
        support: 'Support',
        library: 'Bibliothek',
        settings: 'Einstellungen',
      },
      
      // Login Page
      login: {
        title: 'Willkommen zurück',
        subtitle: 'Melden Sie sich an, um fortzufahren',
        email: 'E-Mail',
        emailPlaceholder: 'sie@firma.de',
        password: 'Passwort',
        passwordPlaceholder: 'Passwort eingeben',
        signIn: 'Anmelden',
        signingIn: 'Wird angemeldet...',
        forgotPassword: 'Passwort vergessen?',
        noAccount: 'Kein Konto?',
        contactAdmin: 'Kontaktieren Sie Ihren Administrator für Zugang',
        welcome: 'Willkommen zurück',
        features: {
          credentials: 'Sichere Zugangsdaten',
          credentialsDesc: 'Verschlüsselter Passwort-Tresor',
          monitoring: 'Statusüberwachung',
          monitoringDesc: 'Website-Status in Echtzeit',
          team: 'Teamverwaltung',
          teamDesc: 'Rollenbasierte Zugriffskontrolle',
        },
        errors: {
          emailRequired: 'Bitte geben Sie Ihre E-Mail ein',
          emailInvalid: 'Bitte geben Sie eine gültige E-Mail ein',
          passwordRequired: 'Bitte geben Sie Ihr Passwort ein',
          invalidCredentials: 'Ungültige Anmeldedaten',
          loginFailed: 'Anmeldung fehlgeschlagen',
        },
        branding: {
          tagline: 'Landeseiten Wartungsmanagement',
        },
      },

      // Dashboard
      dashboard: {
        greeting: {
          morning: 'Guten Morgen',
          afternoon: 'Guten Tag',
          evening: 'Guten Abend',
        },
        subtitle: 'Systemstatus & Team-Verfügbarkeit im Überblick.',
        error: 'Dashboard-Daten konnten nicht geladen werden',
        noTimerRunning: 'Kein Timer läuft',
        startTracking: 'Starten Sie die Zeiterfassung',
        startTimer: 'Timer starten',
        timerRunning: 'Timer läuft',
        stopTimer: 'Stoppen',
        overview: 'Überblick',
        stats: {
          totalProjects: 'Projekte gesamt',
          online: 'Online',
          openTodos: 'Offene Aufgaben',
          supportTickets: 'Support-Tickets',
          atRisk: 'Gefährdet',
          hackedDown: 'Gehackt / Offline',
          pendingApprovals: 'Ausstehende Genehmigungen',
          hacked: 'Kompromittiert',
        },
        suffixes: {
          tasks: 'Aufgaben',
          open: 'Offen',
          warnings: 'Warnungen',
          criticalAlert: 'Kritische Warnung',
          allClear: 'Alles in Ordnung',
        },
        team: {
          availability: 'Team-Verfügbarkeit & Auswirkung',
          overview: 'Teamübersicht',
          teamMember: 'Teammitglied',
          name: 'Name',
          role: 'Rolle',
          status: 'Status',
          returnDate: 'Rückkehrdatum',
          projects: 'Projekte',
          project: 'Projekt',
          noMembers: 'Keine Teammitglieder mit dieser Rolle.',
          na: 'k.A.',
        },
        statuses: {
          sick: 'Krankmeldung',
          vacation: 'Urlaub',
          parental: 'Elternzeit',
        },
        roles: {
          admin: 'Admin',
          pm: 'PM',
          dev: 'Dev',
          viewer: 'Viewer',
        },
        filters: {
          all: 'Alle',
          developers: 'Entwickler',
          managers: 'Manager',
          admins: 'Admins',
        },
        quickActions: {
          title: 'Schnellaktionen',
          startTimer: 'Timer starten',
          viewProjects: 'Projekte anzeigen',
          approvals: 'Genehmigungen',
          reports: 'Berichte anzeigen',
        },
        weeklyHours: {
          title: 'Diese Woche',
        },
        attention: {
          title: 'Benötigt Aufmerksamkeit',
          allGood: 'Alles in Ordnung!',
          noIssues: 'Alle Projekte laufen reibungslos.',
        },
        teamWorkload: {
          title: 'Team-Auslastung',
        },
        // Manager Dashboard
        managerSubtitle: 'Ihre Projekte schreiten voran. Hier ist die Übersicht.',
        managedProjects: 'Verwaltete Projekte',
        projectsAtRisk: 'Gefährdete Projekte',
        teamAvailability: 'Team-Verfügbarkeit',
        needsAttentionSuffix: 'Achtung',
        criticalSuffix: 'Kritisch',
        onlineSuffix: 'Online',
        projectsNeedingAttention: 'Projekte mit Handlungsbedarf',
        teamStatus: 'Teamstatus',
        online: 'Online',
        sickLeave: 'Krankmeldung',
        away: 'Abwesend',
        allOnTrack: 'Alle Projekte auf Kurs! 🎉',
        deadlinePassed: 'Frist abgelaufen am {{date}}',
        criticalInfrastructure: 'Kritische Infrastruktur',
        sitesHackedDown: '{{count}} Seiten gehackt oder offline!',
        // Developer Dashboard
        developerSubtitle: 'Bereit, heute etwas Großartiges zu bauen?',
        hoursToday: 'Stunden heute',
        assignedTasks: 'Zugewiesene Aufgaben',
        highPriority: '{{count}} hohe Priorität',
        myActiveProjects: 'Meine aktiven Projekte',
        noActiveProjects: 'Keine aktiven Projekte gefunden',
        // Widget strings
        myTasks: 'Meine Aufgaben',
        noPendingTasks: 'Keine offenen Aufgaben zugewiesen',
        pendingApprovals: 'Ausstehende Genehmigungen',
        reviewAll: 'Alle prüfen',
        allCaughtUp: 'Alles erledigt! Keine ausstehenden Genehmigungen.',
        new: 'Neu',
        week: 'Woche',
      },

      // Projects
      projects: {
        title: 'Alle Projekte',
        newProject: 'Neues Projekt',
        editProject: 'Projekt bearbeiten',
        createProject: 'Projekt erstellen',
        deleteProject: 'Projekt löschen',
        noProjects: 'Keine Projekte gefunden',
        projectDetails: 'Projektdetails',
        stats: {
          down: 'Offline',
          hacked: 'Gehackt',
          atRisk: 'Gefährdet',
          monitoring: 'Überwachung',
          downTooltip: 'Aktuell offline',
          hackedTooltip: 'Gehackte Websites - KRITISCH',
          atRiskTooltip: 'Kompromittiert oder gefährdet',
          monitoringTooltip: 'Benötigt Aufmerksamkeit',
        },
        filters: {
          allHealth: 'Alle Status',
          allSecurity: 'Alle Sicherheit',
          allManagers: 'Alle Manager',
          allDevelopers: 'Alle Entwickler',
          allTags: 'Alle Tags',
        },
        table: {
          project: 'Projekt',
          pm: 'PM',
          team: 'Team',
          tags: 'Tags',
          todos: 'Aufgaben',
          health: 'Status',
          security: 'Sicherheit',
          actions: 'Aktionen',
        },
        form: {
          name: 'Projektname',
          namePlaceholder: 'Projektname eingeben',
          url: 'Website URL',
          urlPlaceholder: 'https://beispiel.de',
          externalId: 'Externe ID',
          externalIdPlaceholder: 'Externe System-ID',
          clientEmail: 'Kunden-E-Mail',
          clientEmailPlaceholder: 'kunde@beispiel.de',
          clientPhone: 'Kundentelefon',
          manager: 'Projektmanager',
          developer: 'Hauptentwickler',
          developers: 'Teammitglieder',
          selectManager: 'Manager auswählen',
          selectDeveloper: 'Entwickler auswählen',
          selectDevelopers: 'Teammitglieder auswählen',
          tags: 'Tags',
          selectTags: 'Tags auswählen',
          notes: 'Notizen',
          notesPlaceholder: 'Zusätzliche Notizen zu diesem Projekt',
          healthStatus: 'Gesundheitsstatus',
          securityStatus: 'Sicherheitsstatus',
          addMaintenanceTodos: 'Standard-Wartungsaufgaben hinzufügen',
        },
        health: {
          online: 'Online',
          warning: 'Warnung',
          down_error: 'Offline/Fehler',
          confirming_down: 'Wird geprüft...',
          maintenance: 'Wartung',
          unknown: 'Unbekannt',
        },
        uptime: {
          noDataYet: 'Noch keine Daten',
          noDataHint: 'Statistiken erscheinen nach den ersten automatischen Prüfungen',
          checkedAutomatically: 'Wird automatisch geprüft',
          statsError: 'Uptime-Statistiken konnten nicht geladen werden',
          apiKeyConfiguredPlaceholder: '••••••••  (konfiguriert — neuen Schlüssel eingeben, um zu ersetzen)',
        },
        security: {
          secure: 'Sicher',
          monitoring: 'Überwachung',
          compromised: 'Gefährdet',
          hacked: 'Gehackt',
        },
        tabs: {
          todos: 'Aufgaben',
          resources: 'Ressourcen',
          health: 'Status',
          reports: 'Berichte',
          credentials: 'Zugangsdaten',
        },
        messages: {
          created: 'Projekt erfolgreich erstellt',
          updated: 'Projekt erfolgreich aktualisiert',
          deleted: 'Projekt erfolgreich gelöscht',
          createError: 'Projekt konnte nicht erstellt werden',
          updateError: 'Projekt konnte nicht aktualisiert werden',
          deleteError: 'Projekt konnte nicht gelöscht werden',
        },
      },

      // Todos
      todos: {
        title: 'Aufgaben',
        addTodo: 'Aufgabe hinzufügen',
        editTodo: 'Aufgabe bearbeiten',
        deleteTodo: 'Aufgabe löschen',
        noTodos: 'Noch keine Aufgaben',
        task: 'Aufgabe',
        dueDate: 'Fällig am',
        form: {
          title: 'Titel',
          titlePlaceholder: 'Was muss erledigt werden?',
          description: 'Beschreibung',
          descriptionPlaceholder: 'Zusätzliche Details...',
          priority: 'Priorität',
          status: 'Status',
          dueDate: 'Fälligkeitsdatum',
          assignedTo: 'Zugewiesen an',
          selectAssignee: 'Bearbeiter auswählen',
        },
        priority: {
          low: 'Niedrig',
          medium: 'Mittel',
          high: 'Hoch',
          urgent: 'Dringend',
        },
        status: {
          pending: 'Ausstehend',
          in_progress: 'In Bearbeitung',
          completed: 'Abgeschlossen',
          cancelled: 'Abgebrochen',
        },
        messages: {
          created: 'Aufgabe erfolgreich erstellt',
          updated: 'Aufgabe erfolgreich aktualisiert',
          deleted: 'Aufgabe erfolgreich gelöscht',
        },
      },

      // Resources
      resources: {
        title: 'Ressourcen',
        addResource: 'Ressource hinzufügen',
        editResource: 'Ressource bearbeiten',
        deleteResource: 'Ressource löschen',
        noResources: 'Noch keine Ressourcen',
        form: {
          title: 'Titel',
          titlePlaceholder: 'Ressourcentitel',
          type: 'Typ',
          url: 'URL',
          urlPlaceholder: 'https://...',
          file: 'Datei',
          selectFile: 'Datei auswählen',
          isQuickAction: 'Schnellaktion',
          isQuickActionHint: 'In Schnellaktionen anzeigen',
        },
        type: {
          file: 'Datei',
          link: 'Link',
        },
        messages: {
          created: 'Ressource erfolgreich hinzugefügt',
          updated: 'Ressource erfolgreich aktualisiert',
          deleted: 'Ressource erfolgreich gelöscht',
          downloadError: 'Datei konnte nicht heruntergeladen werden',
        },
      },

      // Credentials / Vault
      vault: {
        title: 'Tresor',
        subtitle: 'Sichere Passwortverwaltung',
        addCredential: 'Zugangsdaten hinzufügen',
        addNewCredential: 'Neue Zugangsdaten anlegen',
        editCredential: 'Zugangsdaten bearbeiten',
        deleteCredential: 'Zugangsdaten löschen',
        deleteConfirm: 'Möchten Sie diese Zugangsdaten wirklich löschen?',
        noCredentials: 'Keine Zugangsdaten gespeichert',
        showPassword: 'Passwort anzeigen',
        hidePassword: 'Passwort verbergen',
        copyPassword: 'Passwort kopieren',
        copied: 'Kopiert!',
        share: 'Teilen',
        createShare: 'Freigabelink erstellen',
        noPassword: 'Kein Passwort',
        open: 'Öffnen',
        searchPlaceholder: 'Zugangsdaten suchen...',
        filterByType: 'Nach Typ filtern',
        table: {
          title: 'Bezeichnung',
          type: 'Typ',
          username: 'Benutzername',
          password: 'Passwort',
          url: 'URL',
          actions: 'Aktionen',
        },
        types: {
          all: 'Alle Typen',
          wordpress: 'WordPress',
          ssh: 'SSH',
          ftp: 'FTP',
          database: 'Datenbank',
          hosting: 'Hosting',
          email: 'E-Mail',
          api: 'API',
          apiKey: 'API-Schlüssel',
          other: 'Sonstiges',
        },
        sort: {
          recentlyUpdated: 'Zuletzt aktualisiert',
          title: 'Bezeichnung',
          project: 'Projekt',
        },
        form: {
          label: 'Bezeichnung',
          labelPlaceholder: 'z.B. Admin-Login',
          project: 'Projekt',
          selectProject: 'Projekt auswählen',
          selectType: 'Typ auswählen',
          title: 'Bezeichnung',
          titlePlaceholder: 'Bezeichnung der Zugangsdaten',
          titleHelp: "z.B. 'Produktionsserver', 'Haupt-DB-Benutzer'",
          username: 'Benutzername',
          usernameLogin: 'Benutzername / Login',
          usernamePlaceholder: 'Benutzername oder E-Mail',
          password: 'Passwort',
          passwordApiKey: 'Passwort / API-Schlüssel',
          passwordPlaceholder: 'Passwort eingeben',
          apiKeyPlaceholder: 'API-Schlüssel eingeben',
          leaveBlankToKeep: 'Leer lassen, um aktuelles beizubehalten',
          enterNewPassword: 'Neues Passwort eingeben',
          url: 'URL',
          loginUrl: 'Login-URL (Optional)',
          urlPlaceholder: 'https://...',
          hostnameIp: 'Hostname / IP',
          hostnamePlaceholder: 'z.B. 192.168.1.1 oder db.example.com',
          port: 'Port',
          databaseName: 'Datenbankname',
          notes: 'Notizen',
          notesPlaceholder: 'Zusätzliche Anweisungen...',
          category: 'Kategorie',
          selectCategory: 'Kategorie auswählen',
          selectRequired: 'Bitte wählen Sie ein Projekt',
          typeRequired: 'Bitte wählen Sie einen Typ',
        },
        shareModal: {
          title: 'Zugangsdaten teilen',
          description: 'Erstellen Sie einen sicheren, zeitlich begrenzten Link zum Teilen von',
          linkExpiration: 'Link-Ablauf',
          hour1: '1 Stunde',
          hours24: '24 Stunden',
          days3: '3 Tage',
          days7: '7 Tage',
          maxViews: 'Maximale Aufrufe',
          oneView: '1 Aufruf',
          custom: 'Benutzerdefiniert',
          views: 'Aufrufe',
          passwordProtected: 'Passwortgeschützt',
          noPasswordToggle: 'Kein Passwort',
          accessPassword: 'Zugangspasswort',
          accessPasswordHelp: 'Der Empfänger muss dieses Passwort eingeben, um die Zugangsdaten einzusehen.',
          setPassword: 'Gemeinsames Passwort festlegen',
          passwordRequired: 'Bitte legen Sie ein Passwort fest',
          recipientEmail: 'Empfänger-E-Mail (Optional)',
          recipientPlaceholder: 'empfaenger@beispiel.de',
          generateLink: 'Link erstellen',
          linkCreated: 'Freigabelink erstellt!',
          linkExpires: 'Dieser Link läuft ab am',
          copy: 'Kopieren',
          linkCopied: 'Link in die Zwischenablage kopiert',
          securityNote: 'Sicherheitshinweis',
          securityWarning: 'Jeder mit diesem Link kann auf die Zugangsdaten zugreifen. Teilen Sie ihn sicher.',
          close: 'Schließen',
          success: 'Sicherer Link erstellt!',
          error: 'Link konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
        },
        categories: {
          hosting: 'Hosting',
          cms: 'CMS',
          ftp: 'FTP',
          database: 'Datenbank',
          email: 'E-Mail',
          other: 'Sonstiges',
        },
        messages: {
          created: 'Zugangsdaten erfolgreich hinzugefügt',
          updated: 'Zugangsdaten erfolgreich aktualisiert',
          deleted: 'Zugangsdaten erfolgreich gelöscht',
          revealError: 'Passwort konnte nicht angezeigt werden',
          createError: 'Zugangsdaten konnten nicht hinzugefügt werden',
          updateError: 'Zugangsdaten konnten nicht aktualisiert werden',
        },
      },

      // Library
      library: {
        title: 'Bibliothek',
        subtitle: 'Gemeinsame Dateien, die mit mehreren Projekten verknüpft werden können',
        uploadFile: 'Datei hochladen',
        uploadFirstFile: 'Erste Datei hochladen',
        uploadToLibrary: 'In Bibliothek hochladen',
        editResource: 'Bibliotheks-Ressource bearbeiten',
        noResources: 'Noch keine Bibliotheks-Ressourcen',
        selectFile: 'Datei auswählen',
        selectNewFile: 'Neue Datei auswählen',
        replaceFile: 'Datei ersetzen (optional)',
        file: 'Datei',
        current: 'Aktuell',
        tip: 'Gehen Sie zum Ressourcen-Tab eines Projekts und klicken Sie auf "Aus Bibliothek verknüpfen", um diese Dateien zu verwenden.',
        tipLabel: 'Tipp:',
        clickToDownload: 'Zum Herunterladen klicken',
        selectFileError: 'Bitte wählen Sie eine Datei zum Hochladen aus',
        table: {
          title: 'Bezeichnung',
          category: 'Kategorie',
          file: 'Datei',
          usedIn: 'Verwendet in',
        },
        stats: {
          totalFiles: 'Dateien gesamt',
          inUse: 'In Verwendung',
          categories: 'Kategorien',
        },
        form: {
          title: 'Bezeichnung',
          titlePlaceholder: 'z.B. Entwicklerhandbuch, Sicherheitscheckliste',
          titleRequired: 'Bitte geben Sie einen Titel ein',
          category: 'Kategorie',
          categoryPlaceholder: 'Kategorie auswählen oder eingeben',
          notes: 'Notizen',
          notesPlaceholder: 'Optionale Notizen zu dieser Ressource...',
        },
        deleteConfirm: {
          title: 'Bibliotheks-Ressource löschen?',
          linkedWarning: 'Diese Datei ist mit {{count}} Projekt(en) verknüpft. Sie wird von allen getrennt.',
          noUndo: 'Diese Aktion kann nicht rückgängig gemacht werden.',
        },
        messages: {
          uploaded: 'Bibliotheks-Ressource hochgeladen',
          uploadError: 'Ressource konnte nicht hochgeladen werden',
          updated: 'Bibliotheks-Ressource aktualisiert',
          updateError: 'Ressource konnte nicht aktualisiert werden',
          deleted: 'Bibliotheks-Ressource gelöscht',
          deleteError: 'Ressource konnte nicht gelöscht werden',
          downloadError: 'Datei konnte nicht heruntergeladen werden',
        },
      },

      // Support
      support: {
        subtitle: 'Ticketverwaltung & Kundenkommunikation',
        comingSoon: 'Demnächst verfügbar',
        comingSoonDescription: 'Das Support-Ticket-System befindet sich derzeit in Entwicklung. Bleiben Sie dran — diese Funktion wird bald verfügbar sein!',
      },

      // Team
      team: {
        title: 'Team',
        subtitle: 'Teammitglieder verwalten',
        addMember: 'Teammitglied hinzufügen',
        editMember: 'Mitglied bearbeiten',
        deleteMember: 'Mitglied löschen',
        noMembers: 'Keine Teammitglieder gefunden',
        viewProjects: 'Projekte anzeigen',
        searchPlaceholder: 'Team durchsuchen...',
        filterByRole: 'Nach Rolle filtern',
        filterByTag: 'Nach Tag filtern',
        filters: {
          allRoles: 'Alle Rollen',
          allTags: 'Alle Tags',
        },
        table: {
          member: 'Mitglied',
          user: 'Benutzer',
          role: 'Rolle',
          email: 'E-Mail',
          phone: 'Telefon',
          tags: 'Tags',
          projects: 'Projekte',
          hourlyRate: 'Stundensatz',
          status: 'Status',
          actions: 'Aktionen',
          adminAccess: 'Admin-Zugang',
        },
        form: {
          name: 'Vollständiger Name',
          namePlaceholder: 'Namen eingeben',
          email: 'E-Mail',
          emailPlaceholder: 'email@beispiel.de',
          phone: 'Telefon',
          phonePlaceholder: '+49 123 456 789',
          role: 'Rolle',
          selectRole: 'Rolle auswählen',
          password: 'Passwort',
          passwordPlaceholder: 'Passwort eingeben',
          passwordHint: 'Leer lassen um aktuelles Passwort zu behalten',
          newPassword: 'Neues Passwort',
          newPasswordPlaceholder: 'Neues Passwort eingeben',
          tags: 'Tags',
          selectTags: 'Tags auswählen',
          hourlyRate: 'Stundensatz',
          hourlyRatePlaceholder: 'z.B. 50.00',
          adminAccess: 'Admin-Zugang',
          adminAccessHint: 'Diesem Benutzer volle Admin-Rechte gewähren, ohne seine Rolle zu ändern',
          generatePassword: 'Passwort generieren',
          passwordGenerated: 'Passwort generiert',
        },
        passwordRules: {
          minLength: 'Mindestens 8 Zeichen',
          uppercase: 'Mindestens ein Großbuchstabe',
          lowercase: 'Mindestens ein Kleinbuchstabe',
          number: 'Mindestens eine Zahl',
          special: 'Mindestens ein Sonderzeichen (!@#$%^&*)',
        },
        roles: {
          admin: 'Admin',
          manager: 'Manager',
          developer: 'Entwickler',
        },
        messages: {
          created: 'Teammitglied erfolgreich hinzugefügt',
          updated: 'Teammitglied erfolgreich aktualisiert',
          deleted: 'Teammitglied erfolgreich gelöscht',
          passwordReset: 'Passwort erfolgreich zurückgesetzt',
          passwordResetError: 'Passwort konnte nicht zurückgesetzt werden',
          resetLinkSent: 'Link zum Zurücksetzen gesendet',
          resetLinkError: 'Link konnte nicht gesendet werden',
        },
        resetPassword: 'Passwort zurücksetzen',
        resetPasswordTitle: 'Passwort zurücksetzen',
        resetPasswordDesc: 'Neues Passwort für {{name}} festlegen',
        sendResetLink: 'Reset-Link senden',
        sendResetLinkConfirm: 'Einen Passwort-Reset-Link an {{email}} senden?',
      },

      // Tags
      tags: {
        title: 'Tags',
        subtitle: 'Projekte und Teammitglieder organisieren',
        addTag: 'Tag hinzufügen',
        createTag: 'Tag erstellen',
        editTag: 'Tag bearbeiten',
        deleteTag: 'Tag löschen',
        noTags: 'Noch keine Tags erstellt',
        newTag: 'Neuer Tag',
        table: {
          tag: 'Tag',
          slug: 'Slug',
          projects: 'Projekte',
          projectCount: '{{count}} Projekte',
          actions: 'Aktionen',
        },
        form: {
          name: 'Tag-Name',
          namePlaceholder: 'Tag-Namen eingeben',
          nameRequired: 'Bitte geben Sie einen Tag-Namen ein',
          slug: 'Slug',
          slugPlaceholder: 'automatisch generiert',
          color: 'Farbe',
          selectColor: 'Farbe auswählen',
        },
        colors: {
          default: 'Standard',
          blue: 'Blau',
          green: 'Grün',
          red: 'Rot',
          orange: 'Orange',
          purple: 'Lila',
          cyan: 'Cyan',
          magenta: 'Magenta',
          gold: 'Gold',
        },
        messages: {
          created: 'Tag erfolgreich erstellt',
          updated: 'Tag erfolgreich aktualisiert',
          deleted: 'Tag erfolgreich gelöscht',
        },
      },

      // Activity Log
      activity: {
        title: 'Aktivitätsprotokoll',
        subtitle: 'Prüfprotokoll aller Systemaktivitäten',
        searchPlaceholder: 'Aktivitäten durchsuchen...',
        subjectType: 'Betreff-Typ',
        noActivity: 'Noch keine Aktivitäten',
        loadError: 'Aktivitäten konnten nicht geladen werden',
        table: {
          user: 'Benutzer',
          action: 'Aktion',
          subject: 'Betreff',
          time: 'Zeit',
        },
        types: {
          all: 'Alle Typen',
          project: 'Projekt',
          credential: 'Zugangsdaten',
          todo: 'Aufgabe',
          user: 'Benutzer',
          unknown: 'Unbekannt',
          system: 'System',
        },
      },

      // Settings
      settings: {
        title: 'Einstellungen',
        subtitle: 'Globale Anwendungseinstellungen konfigurieren. Änderungen werden sofort übernommen.',
        accessDenied: 'Zugriff verweigert',
        noPermission: 'Sie haben keine Berechtigung, auf diese Seite zuzugreifen.',
        loadError: 'Einstellungen konnten nicht geladen werden',
        loadErrorHint: 'Bitte versuchen Sie, die Seite neu zu laden.',
        saveSuccess: 'Einstellungen erfolgreich gespeichert',
        saveError: 'Einstellungen konnten nicht gespeichert werden',
        reset: 'Zurücksetzen',
        saveSettings: 'Einstellungen speichern',
        uptime: {
          title: 'Uptime-Überwachung',
          enabled: 'Automatische Überwachung',
          enabledTooltip: 'Automatische Uptime-Prüfungen für alle Projekte aktivieren oder deaktivieren',
          enabledOn: 'Aktiviert',
          enabledOff: 'Deaktiviert',
          interval: 'Prüfintervall',
          intervalTooltip: 'Wie oft Websites geprüft werden (in Minuten)',
          intervalOptions: {
            min1: 'Alle 1 Minute',
            min2: 'Alle 2 Minuten',
            min3: 'Alle 3 Minuten',
            min5: 'Alle 5 Minuten (Empfohlen)',
            min10: 'Alle 10 Minuten',
            min15: 'Alle 15 Minuten',
            min30: 'Alle 30 Minuten',
          },
          concurrency: 'Parallelität',
          concurrencyTooltip: 'Anzahl der gleichzeitig geprüften Websites',
          concurrencyUnit: 'Seiten gleichzeitig',
          timeout: 'Zeitüberschreitung',
          timeoutTooltip: 'Wie lange auf eine Antwort gewartet wird, bevor die Website als down markiert wird',
          timeoutUnit: 'Sekunden',
          tip: 'Tipp: Bei ca. 200 Websites mit Parallelität 15 und 5-Minuten-Intervall werden Prüfungen in ca. 2-3 Minuten abgeschlossen.',
        },
        backup: {
          title: 'Backup-Einstellungen',
          storageLocation: 'Speicherort',
          active: 'Aktiv',
          storageDriverInfo: 'Der Speichertreiber wird über Umgebungsvariablen (.env) konfiguriert. Ändern Sie BACKUP_DRIVER zum Umschalten.',
          defaultFrequency: 'Standard-Häufigkeit',
          frequencyTooltip: 'Standard-Backup-Häufigkeit für neue Projekte',
          daily: 'Täglich',
          weekly: 'Wöchentlich',
          monthly: 'Monatlich',
          retentionPeriod: 'Aufbewahrungszeitraum',
          retentionTooltip: 'Wie lange Backups aufbewahrt werden, bevor sie automatisch gelöscht werden',
          retentionUnit: 'Tage',
          scheduledBackups: 'Geplante Backups',
          frequency: 'Häufigkeit',
          time: 'Zeit',
          retentionPolicy: 'Aufbewahrungsrichtlinie',
          maxBackups: 'Max. Backups',
          maxAge: 'Max. Alter',
          minKeep: 'Min. behalten',
          defaultContents: 'Standard-Backup-Inhalte',
          database: 'Datenbank',
          files: 'Dateien',
          uploads: 'Uploads',
        },
        drivers: {
          local: 'Lokaler Speicher (Server)',
          s3: 'Amazon S3',
          gcs: 'Google Cloud Storage',
          gdrive: 'Google Drive',
        },
      },

      // GDPR Audit
      gdpr: {
        title: 'DSGVO-Audit',
        subtitle: 'Headless-Browser-Scan für Cookies, Tracker & Einwilligungskonformität',
        quick: 'Schnell',
        full: 'Vollständig',
        runQuickAudit: 'Schnell-Audit starten',
        runFullAudit: 'Voll-Audit starten',
        rerunQuick: 'Schnell-Audit wiederholen',
        rerunFull: 'Voll-Audit wiederholen',
        pdf: 'PDF',
        viewPdf: 'PDF ansehen',
        saveToReports: 'In Berichte speichern',
        reportSaved: 'Bericht erfolgreich gespeichert',
        reportSaveError: 'Bericht konnte nicht gespeichert werden',
        running: {
          title: '{{mode}}-Audit läuft...',
          subtitle: 'Der Headless-Browser scannt die Website. Dies kann bis zu 60 Sekunden dauern.',
        },
        noAudit: {
          title: 'Noch kein DSGVO-Audit',
          description: 'Starten Sie ein Schnell- oder Voll-Audit, um Tracking, Cookies und Einwilligungskonformität zu analysieren.',
        },
        error: {
          title: 'Audit fehlgeschlagen',
          retry: 'Audit wiederholen',
        },
        score: {
          criticalIssues: 'Kritische Probleme',
          needsWork: 'Verbesserungsbedarf',
          good: 'Gut',
          excellent: 'Ausgezeichnet',
        },
        stats: {
          trackers: 'Tracker',
          trackingCookies: 'Tracking-Cookies',
          banner: 'Banner',
          detected: 'Erkannt',
          notFound: 'Nicht gefunden',
          acceptFlow: 'Akzeptieren-Flow',
          rejectFlow: 'Ablehnen-Flow',
          working: 'Funktioniert',
          noActivity: 'Keine Aktivität',
          clean: 'Sauber',
          leaking: 'Undicht!',
        },
        badges: {
          fullAudit: 'Voll-Audit',
          quickScan: 'Schnell-Scan',
          aiEnhanced: 'KI-Erweitert',
          basicScan: 'Basis-Scan',
        },
        ai: {
          title: 'KI-Konformitätsanalyse',
          violations: 'Verstöße',
          whatsRight: 'Was richtig gemacht wird',
          recommendations: 'Empfehlungen',
          severity: 'Schweregrad',
          issue: 'Problem',
          details: 'Details',
          legalRef: 'Rechtsgrundlage',
          fix: 'Lösung',
        },
        sections: {
          auditChecks: 'Audit-Prüfungen',
          cookieBanner: 'Cookie-Banner-Analyse',
          trackingServices: 'Tracking-Dienste (vor Einwilligung)',
          allCookies: 'Alle Cookies',
          acceptAllFlow: 'Alle-Akzeptieren-Flow',
          rejectFlow: 'Ablehnen-Flow',
        },
        table: {
          check: 'Prüfung',
          status: 'Status',
          details: 'Details',
          service: 'Dienst',
          requests: 'Anfragen',
          name: 'Name',
          value: 'Wert',
          domain: 'Domain',
          tracking: 'Tracking',
        },
        bannerLabels: {
          detected: 'Erkannt',
          solution: 'Lösung',
          hasRejectOption: 'Hat Ablehnen-Option',
          preTickedCheckboxes: 'Vorab angekreuzte Checkboxen',
        },
        metadata: {
          mode: 'Modus',
          scannedAt: 'Gescannt',
          duration: 'Dauer',
          seconds: '{{count}}s',
        },
      },

      // Barrierefreiheits-Audit
      accessibility: {
        title: 'Barrierefreiheits-Audit',
        subtitle: 'WCAG 2.1 Level AA Konformitätsprüfung',
        noUrl: 'Keine Website-URL konfiguriert',
        noUrlHint: 'Fügen Sie eine URL hinzu, um die Barrierefreiheitsprüfung zu aktivieren',
        runAudit: 'Audit starten',
        rerunAudit: 'Audit wiederholen',
        pdf: 'PDF',
        viewPdf: 'PDF ansehen',
        saveToReports: 'In Berichte speichern',
        reportSaved: 'Bericht erfolgreich gespeichert',
        reportSaveError: 'Bericht konnte nicht gespeichert werden',
        auditComplete: 'Barrierefreiheits-Audit abgeschlossen!',
        auditFailed: 'Barrierefreiheits-Audit fehlgeschlagen',
        lastAudit: 'Zuletzt geprüft',
        running: {
          title: 'Barrierefreiheits-Audit läuft...',
          subtitle: 'Prüfung auf WCAG 2.1 Verstöße — dies kann 15-20 Sekunden dauern',
        },
        noAudit: {
          title: 'Noch kein Barrierefreiheits-Audit',
          description: 'Starten Sie ein Audit, um diese Website auf WCAG 2.1 Level AA Verstöße zu prüfen',
        },
        score: {
          accessible: 'Barrierefrei',
          needsWork: 'Verbesserungsbedarf',
          criticalIssues: 'Kritische Probleme',
        },
        stats: {
          violations: 'Verstöße',
          critical: 'Kritisch',
          serious: 'Schwerwiegend',
          passed: 'Bestanden',
        },
        badges: {
          aiEnhanced: 'KI-Erweitert',
          automatedScan: 'Automatischer Scan',
        },
        ai: {
          title: 'KI-Barrierefreiheitsanalyse',
          violations: 'Verstöße',
          whatsRight: 'Was richtig gemacht wird',
          recommendations: 'Empfehlungen',
          impact: 'Auswirkung',
          issue: 'Problem',
          details: 'Details',
          wcag: 'WCAG',
          fix: 'Lösung',
        },
        sections: {
          violationsByCategory: 'Verstöße nach Kategorie',
          customChecks: 'Zusätzliche Prüfungen',
          passed: 'Bestandene Prüfungen',
          needsReview: 'Manuelle Prüfung erforderlich',
        },
        table: {
          rule: 'Regel',
          elements: 'Elemente',
          example: 'Beispiel',
          check: 'Prüfung',
          details: 'Details',
          category: 'Kategorie',
        },
      },

      // Time Tracking
      time: {
        title: 'Meine Zeit',
        subtitle: 'Arbeitszeit erfassen',
        today: 'Heute',
        thisWeek: 'Diese Woche',
        thisMonth: 'Diesen Monat',
        total: 'Gesamt',
        hours: 'Stunden',
        minutes: 'Minuten',
        startTimer: 'Timer starten',
        stopTimer: 'Timer stoppen',
        addEntry: 'Eintrag hinzufügen',
        editEntry: 'Zeiteintrag bearbeiten',
        addTimeEntry: 'Zeiteintrag hinzufügen',
        deleteEntry: 'Eintrag löschen',
        noEntries: 'Noch keine Zeiteinträge',
        week: 'Woche',
        month: 'Monat',
        all: 'Alle',
        from: 'Von',
        to: 'Bis',
        todaysTotal: 'Heutiges Gesamt',
        weekTotal: 'KW {{week}} Gesamt',
        monthTotal: '{{month}} Gesamt',
        allTimeTotal: 'Gesamtzeit',
        entries: 'Einträge',
        draftUnsubmitted: 'Entwurf (nicht eingereicht)',
        entriesCount: '({{count}} Einträge)',
        table: {
          project: 'Projekt',
          description: 'Beschreibung',
          time: 'Zeit',
          duration: 'Dauer',
          status: 'Status',
          actions: 'Aktionen',
          noDescription: 'Keine Beschreibung',
        },
        selection: {
          selected: '{{count}} Entwurfs-Eintrag ausgewählt ({{duration}})',
          selected_plural: '{{count}} Entwurfs-Einträge ausgewählt ({{duration}})',
          clearSelection: 'Auswahl aufheben',
          submitSelected: 'Ausgewählte zur Genehmigung einreichen',
          selectAllDraft: 'Alle Entwürfe auswählen',
          submitSuccess: 'Einträge zur Genehmigung eingereicht',
          submitError: 'Einreichung fehlgeschlagen',
        },
        empty: {
          today: 'Keine Einträge heute. Starten Sie den Timer!',
          all: 'Keine Einträge gefunden. Nutzen Sie den Datumsbereich zum Filtern.',
          period: 'Keine Einträge für diesen {{period}}.',
        },
        deleteConfirm: 'Diesen Eintrag löschen?',
        totalEntries: '{{total}} Einträge',
        cancel: 'Abbrechen',
        update: 'Aktualisieren',
        form: {
          project: 'Projekt',
          selectProject: 'Projekt auswählen...',
          projectRequired: 'Bitte wählen Sie ein Projekt',
          description: 'Beschreibung',
          descriptionPlaceholder: 'Woran haben Sie gearbeitet?',
          date: 'Datum',
          startTime: 'Startzeit',
          endTime: 'Endzeit',
          duration: 'Dauer',
          billable: 'Abrechenbar',
        },
        messages: {
          started: 'Timer gestartet',
          stopped: 'Timer gestoppt',
          created: 'Zeiteintrag hinzugefügt',
          updated: 'Zeiteintrag aktualisiert',
          deleted: 'Zeiteintrag gelöscht',
        },
      },

      // Approvals
      approvals: {
        title: 'Genehmigungen',
        subtitle: 'Stundenzettel prüfen und genehmigen',
        pending: 'Ausstehend',
        approved: 'Genehmigt',
        rejected: 'Abgelehnt',
        approve: 'Genehmigen',
        reject: 'Ablehnen',
        approveAll: 'Alle genehmigen',
        noApprovals: 'Keine ausstehenden Genehmigungen',
        timesheet: 'Stundenzettel',
        submittedBy: 'Eingereicht von',
        submittedAt: 'Eingereicht am',
        totalHours: 'Gesamtstunden',
        table: {
          developer: 'Entwickler',
          week: 'Woche',
          totalTime: 'Gesamtzeit',
          entries: 'Einträge',
          submitted: 'Eingereicht',
          actions: 'Aktionen',
          review: 'Prüfen',
        },
        awaitingApproval: 'Stundenzettel warten auf Ihre Genehmigung',
        details: {
          timesheetTitle: 'Stundenzettel von {{name}}',
          totalTime: 'Gesamtzeit',
          entries: 'Einträge',
          defaultRate: 'Standardsatz',
          perHour: '{{rate}}/Std.',
          project: 'Projekt',
          description: 'Beschreibung',
          noDescription: 'Keine Beschreibung',
          date: 'Datum',
          duration: 'Dauer',
          ratePerHour: 'Satz (€/Std.)',
          close: 'Schließen',
        },
        selection: {
          selected: 'Ausgewählt: {{count}} Einträge',
          approveSelected: 'Ausgewählte genehmigen ({{count}})',
          rejectSelected: 'Ausgewählte ablehnen ({{count}})',
          selectWarning: 'Bitte wählen Sie mindestens einen Eintrag',
        },
        rejectModal: {
          title: 'Ausgewählte Einträge ablehnen',
          description: 'Bitte geben Sie einen Grund für die Ablehnung von {{count}} Einträgen an. Der Entwickler wird benachrichtigt.',
          placeholder: 'Grund für die Ablehnung...',
          reasonRequired: 'Bitte geben Sie einen Ablehnungsgrund an',
          okText: '{{count}} Einträge ablehnen',
        },
        messages: {
          approved: 'Ausgewählte Einträge genehmigt!',
          rejected: 'Ausgewählte Einträge abgelehnt',
          approveError: 'Genehmigung fehlgeschlagen',
          rejectError: 'Ablehnung fehlgeschlagen',
          invoiceCreated: 'Rechnung #{{number}} erstellt',
        },
      },

      // Invoices
      invoices: {
        title: 'Rechnungen',
        subtitle: 'Abrechnung und Zahlungen verwalten',
        newInvoice: 'Neue Rechnung',
        noInvoices: 'Keine Rechnungen gefunden',
        invoice: 'Rechnung',
        invoiceNumber: 'Rechnungs-Nr.',
        amount: 'Betrag',
        status: {
          draft: 'Entwurf',
          pending: 'Ausstehend',
          approved: 'Genehmigt',
          paid: 'Bezahlt',
          declined: 'Abgelehnt',
        },
        table: {
          invoiceNumber: 'Rechnungs-Nr.',
          developer: 'Entwickler',
          period: 'Zeitraum',
          hours: 'Stunden',
          amount: 'Betrag',
          status: 'Status',
          actions: 'Aktionen',
          view: 'Ansehen',
          pay: 'Bezahlen',
        },
        filters: {
          from: 'Von',
          to: 'Bis',
          allDevelopers: 'Alle Entwickler',
          allStatus: 'Alle Status',
          thisMonth: 'Dieser Monat',
          lastMonth: 'Letzter Monat',
          thisYear: 'Dieses Jahr',
        },
        stats: {
          totalInvoiced: 'Gesamt fakturiert',
          pendingPayment: 'Ausstehende Zahlung',
          paid: 'Bezahlt',
        },
        details: {
          title: 'Rechnungsdetails',
          developer: 'Entwickler',
          period: 'Zeitraum',
          totalHours: 'Gesamtstunden',
          total: 'Gesamt',
          close: 'Schließen',
          timeEntries: 'Zeiteinträge',
          project: 'Projekt',
          description: 'Beschreibung',
          duration: 'Dauer',
          noProject: 'Kein Projekt',
          noEntries: 'Keine Einträge',
        },
        actions: {
          approve: 'Genehmigen',
          decline: 'Ablehnen',
          markPaid: 'Als bezahlt markieren',
          download: 'PDF herunterladen',
          exportCsv: 'CSV exportieren',
        },
        messages: {
          approved: 'Rechnung genehmigt!',
          declined: 'Rechnung abgelehnt',
          paid: 'Rechnung als bezahlt markiert!',
          approveError: 'Genehmigung fehlgeschlagen',
          declineError: 'Ablehnung fehlgeschlagen',
          paidError: 'Bezahlung fehlgeschlagen',
          exported: 'Rechnungen als CSV exportiert',
          noExport: 'Keine Rechnungen zum Exportieren',
        },
        apiError: 'Rechnungs-API nicht verfügbar',
        apiErrorDesc: 'Das Rechnungsmodul konnte nicht geladen werden. Bitte starten Sie den Entwicklungsserver neu.',
      },

      // Analytics
      analytics: {
        title: 'Zeitanalyse',
        subtitle: 'Einblicke und Trends aus der Zeiterfassung Ihres Teams',
        totalHours: 'Gesamtstunden',
        totalEntries: 'Gesamt-Einträge',
        activeProjects: 'Aktive Projekte',
        avgHoursDay: 'Durchschn. Std./Tag',
        weeklyTrend: 'Wochentrend',
        timeByProject: 'Zeit nach Projekt',
        teamLeaderboard: 'Team-Rangliste',
        projectDistribution: 'Projektverteilung',
        noData: 'Keine Daten für diesen Zeitraum',
        hours: 'Stunden',
        table: {
          developer: 'Entwickler',
          hours: 'Stunden',
          entries: 'Einträge',
          avgPerEntry: 'Durchschn./Eintrag',
        },
        presets: {
          last7: 'Letzte 7 Tage',
          last30: 'Letzte 30 Tage',
          thisMonth: 'Dieser Monat',
          lastMonth: 'Letzter Monat',
        },
      },

      // Financial Reports
      reports: {
        title: 'Finanzberichte',
        subtitle: 'Genehmigte Zeiteinträge anzeigen und Zahlungen verarbeiten',
        addReport: 'Bericht hinzufügen',
        editReport: 'Bericht bearbeiten',
        deleteReport: 'Bericht löschen',
        noReports: 'Noch keine Wartungsberichte',
        downloadPdf: 'PDF herunterladen',
        form: {
          date: 'Berichtsdatum',
          duration: 'Dauer (Minuten)',
          durationPlaceholder: 'z.B. 60',
          summary: 'Zusammenfassung',
          summaryPlaceholder: 'Kurze Zusammenfassung der durchgeführten Arbeiten',
          issuesFound: 'Gefundene Probleme',
          issuesResolved: 'Behobene Probleme',
          addIssue: 'Problem hinzufügen',
        },
        messages: {
          created: 'Bericht erfolgreich erstellt',
          updated: 'Bericht erfolgreich aktualisiert',
          deleted: 'Bericht erfolgreich gelöscht',
        },
      },

      // Availability
      availability: {
        title: 'Verfügbarkeit festlegen',
        setStatus: 'Status setzen',
        setStatusFor: 'Status setzen für',
        editAbsence: 'Abwesenheit bearbeiten',
        cancelAbsence: 'Abwesenheit stornieren',
        selectMember: 'Teammitglied',
        selectMemberPlaceholder: 'Leer lassen für sich selbst',
        statusLabel: 'Status',
        duration: 'Dauer',
        reason: 'Grund (Optional)',
        statusSet: 'Verfügbarkeitsstatus erfolgreich gesetzt',
        updated: 'Verfügbarkeit erfolgreich aktualisiert',
        cancelled: 'Abwesenheit erfolgreich storniert',
        error: 'Verfügbarkeit konnte nicht aktualisiert werden',
        confirmCancel: 'Möchten Sie diese Abwesenheit wirklich stornieren?',
        setByAdmin: 'Gesetzt von {{name}}',
        absenceIndicator: '{{status}} bis {{date}}',
        absenceNoEnd: '{{status}} — kein Enddatum',
        status: {
          available: 'Verfügbar',
          sick: 'Krankgeschrieben',
          vacation: 'Urlaub',
          parental: 'Elternzeit',
          remote: 'Remote',
          half_day: 'Halber Tag',
          other: 'Sonstiges',
        },
        form: {
          status: 'Status',
          note: 'Notiz',
          notePlaceholder: 'Optionale Notiz...',
          until: 'Bis',
        },
        messages: {
          updated: 'Verfügbarkeit aktualisiert',
        },
      },

      // Theme
      theme: {
        light: 'Hell',
        dark: 'Dunkel',
        system: 'System',
      },

      // Language
      language: {
        en: 'English',
        de: 'Deutsch',
      },

      // Errors
      errors: {
        generic: 'Etwas ist schiefgelaufen',
        notFound: 'Nicht gefunden',
        unauthorized: 'Nicht autorisiert',
        forbidden: 'Zugriff verweigert',
        network: 'Netzwerkfehler',
        validation: 'Validierungsfehler',
      },

      // Integration Tokens
      integrationTokens: {
        card: {
          title: 'API & Integrationen',
          createButton: 'Token erstellen',
          description:
            'Langlebige Tokens für KI-Clients über MCP. Jeder Token gilt nur für die gewählten Berechtigungen — und nie für mehr, als deine Rolle ohnehin darf.',
          revokeAllNotice:
            'Mit „Überall abmelden“ oder bei einer Passwortänderung werden auch alle deine Integrations-Tokens widerrufen.',
        },
        table: {
          name: 'Name',
          scopes: 'Berechtigungen',
          expiry: 'Gültigkeit',
          lastUsed: 'Zuletzt verwendet',
        },
        empty: 'Noch keine Integrations-Tokens',
        neverUsed: 'Nie verwendet',
        neverExpires: 'Läuft nie ab',
        expired: 'Abgelaufen',
        expiresIn: 'läuft {{relative}} ab',
        revoke: 'Widerrufen',
        revokeConfirm: {
          title: 'Token „{{name}}“ widerrufen?',
          content: 'Jeder Client, der diesen Token verwendet, verliert sofort den Zugriff. Das lässt sich nicht rückgängig machen.',
          okText: 'Widerrufen',
          cancelText: 'Abbrechen',
        },
        toasts: {
          revoked: 'Token widerrufen',
          revokeError: 'Token konnte nicht widerrufen werden',
          createError: 'Token konnte nicht erstellt werden. Passwort korrekt?',
          copied: 'In die Zwischenablage kopiert',
        },
        scopeTags: {
          read: 'Lesen',
          write: 'Schreiben',
          wp: 'WordPress',
          wpDestructive: 'WP kritisch',
        },
        scopes: {
          read: {
            label: 'Lesen',
            hint: 'Projekte, Todos, Zeiten und Team einsehen. Ändert nichts.',
          },
          write: {
            label: 'Schreiben',
            hint: 'Todos, Zeiterfassung und Projektdaten anlegen und ändern.',
          },
          wp: {
            label: 'WordPress',
            hint: 'Wartungsmodus, Cache, Updates und Backups auf Kundenseiten. Umkehrbar.',
          },
          wpDestructive: {
            label: 'WordPress — kritisch',
            hint: 'Notfall-Wiederherstellung, Backup-Restore und Massenaktionen über alle Seiten. Nicht umkehrbar.',
          },
        },
        expiryOptions: {
          days30: '30 Tage',
          days90: '90 Tage',
          year1: '1 Jahr',
          never: 'Läuft nie ab',
        },
        create: {
          title: 'Neuen Integrations-Token erstellen',
          createdTitle: 'Token erstellt',
          nameLabel: 'Name',
          namePlaceholder: 'Claude Code — MacBook',
          nameRequired: 'Bitte einen Namen angeben',
          nameHelp: 'Wofür ist dieser Token? z. B. „Claude Code — MacBook“',
          scopesLabel: 'Berechtigungen',
          scopesRequired: 'Mindestens eine Berechtigung wählen',
          scopeUnavailable: ' — für deine Rolle nicht verfügbar',
          expiryLabel: 'Gültigkeit',
          passwordLabel: 'Aktuelles Passwort',
          passwordRequired: 'Passwort zur Bestätigung eingeben',
          passwordHelp: 'Zur Bestätigung, dass du das wirklich bist.',
          cancel: 'Abbrechen',
          submit: 'Token erstellen',
          done: 'Fertig — ich habe den Token gespeichert',
          revealWarningTitle: 'Dieser Token wird nur einmal angezeigt.',
          revealWarningDescription:
            'Kopiere ihn jetzt. Danach lässt er sich nicht wieder anzeigen — nur widerrufen und neu erstellen.',
          tokenLabel: 'Token',
          copyToken: 'Token kopieren',
          connectClient: 'Client verbinden',
          connectInstructions: 'Diesen Befehl im Terminal ausführen:',
          copyCommand: 'Befehl kopieren',
        },
      },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,
    
    interpolation: {
      escapeValue: false, // React already escapes
    },
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'lsm-language',
    },
  });

export default i18n;
